import { z } from "zod";

import {
  VibeEnvVarNameSchema,
  VibeEnvVarScopeReadSchema,
  VibeEnvVarScopeSchema
} from "./env-vars.schemas";

/**
 * Vault path the secret resolves to. Format is deterministic:
 *   `secret/data/<orgId>/<appId>/<NAME>`
 *
 * The backend computes this from (organizationId, vibeAppId, name) at
 * entity construction time — we never accept it from the API caller.
 * The schema validates it on READ paths (so a stored row matches the
 * format we expect) but the WRITE body (UpsertVibeAppSecretRefBodySchema)
 * does NOT include it as an input field.
 */
export const VibeVaultPathSchema = z
  .string()
  .min(1, "vaultPath is required")
  .regex(
    /^secret\/data\/[^/]+\/[0-9a-f-]{36}\/[A-Z_][A-Z0-9_]*$/,
    "vaultPath must follow secret/data/<orgId>/<appUuid>/<NAME>"
  );

export const VibeAppSecretRefSchema = z.object({
  id: z.string().uuid(),
  vibeAppId: z.string().uuid(),
  organizationId: z.string(),
  name: VibeEnvVarNameSchema,
  vaultPath: VibeVaultPathSchema,
  scope: VibeEnvVarScopeReadSchema,
  createdByUserId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type VibeAppSecretRefDto = z.infer<typeof VibeAppSecretRefSchema>;

// ----------------------------------------------------------------------------
// Upsert secret ref
// ----------------------------------------------------------------------------

export const UpsertVibeAppSecretRefPathVarsSchema = z.object({
  appId: z.string().uuid()
});

/**
 * Body intentionally has NO value field — this entity is the pointer,
 * not the carrier. Writing the actual secret value happens through a
 * separate Vault-only endpoint (later slice once the Vault adapter
 * lands) so the access-control surface stays separate from the
 * pointer registry.
 */
export const UpsertVibeAppSecretRefBodySchema = z.object({
  name: VibeEnvVarNameSchema,
  scope: VibeEnvVarScopeSchema.optional()
});

export const UpsertVibeAppSecretRefResponseSchema = z.object({
  secretRef: VibeAppSecretRefSchema
});

// ----------------------------------------------------------------------------
// Set secret value (stage for Vault delivery)
// ----------------------------------------------------------------------------

/**
 * The value-write path. Unlike the pointer upsert above, this carries the
 * actual secret VALUE. The control plane KMS-encrypts it under the tenant's
 * own recovery-seal CMK and parks it as a pending write; the tenant's agent
 * decrypts it on its next sync and writes it into the cluster's Vault. The
 * control plane can stage but never read the value back.
 *
 * Keyed by secretRefId (not name) because (app, scope, name) — name alone is
 * ambiguous across scopes; the ref id is unambiguous and is what List returns.
 *
 * The 4096-byte ceiling is the KMS `Encrypt` plaintext limit — enforced here
 * so an over-long value fails at the boundary, not at the KMS call. It is a
 * BYTE limit, not a character count: a multi-byte value ≤4096 chars can still
 * exceed 4096 UTF-8 bytes, so we measure encoded length (`TextEncoder`, which
 * works in both the Node backend and the browser bundle — `Buffer` would not).
 */
export const SetVibeAppSecretValuePathVarsSchema = z.object({
  appId: z.string().uuid(),
  secretRefId: z.string().uuid()
});

export const SetVibeAppSecretValueBodySchema = z.object({
  value: z
    .string()
    .min(1, "value is required")
    .refine((v) => new TextEncoder().encode(v).length <= 4096, {
      message: "value must be at most 4096 bytes (KMS Encrypt plaintext limit)"
    })
});

/**
 * 202-style ack. Returns the pending-write summary — NEVER the value or the
 * ciphertext. `pendingWriteId` lets a caller correlate the eventual
 * SECRET_VALUE_WRITTEN audit event; `stagedAt` is the at-rest window's start.
 */
export const SetVibeAppSecretValueResponseSchema = z.object({
  secretRefId: z.string().uuid(),
  pendingWriteId: z.string().uuid(),
  stagedAt: z.coerce.date()
});

// ----------------------------------------------------------------------------
// List secret refs
// ----------------------------------------------------------------------------

export const ListVibeAppSecretRefsPathVarsSchema = z.object({
  appId: z.string().uuid()
});

export const ListVibeAppSecretRefsResponseSchema = z.object({
  secretRefs: z.array(VibeAppSecretRefSchema)
});

// ----------------------------------------------------------------------------
// Delete secret ref
// ----------------------------------------------------------------------------

export const DeleteVibeAppSecretRefPathVarsSchema = z.object({
  appId: z.string().uuid(),
  secretRefId: z.string().uuid()
});

export const DeleteVibeAppSecretRefResponseSchema = z.object({
  deletedId: z.string().uuid()
});
